import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { Box, Grid, Collapse } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useInterval } from 'common/hooks/useInterval';
import Container from 'components/Container';
import { Duration } from 'common/types';
import { getTimeNearestToNextQuarterHour, getNewEndTime } from 'common/utils/dateCalculations';

import * as NewRentalActions from 'actions/NewRentalActions';
import * as ViewActions from 'actions/ViewActions';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';

import RentNowToggle from './RentNowToggle';
import DurationSelect from './DurationSelect';
import StartTimeInput from './StartTimeInput';
import { TimeSelectorArgs } from 'components/TimeSelector/types';

const TimeLocationSelector = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const isRentNow = useSelector(ViewSelectors.newRentalToggled);
	const startDate = useSelector(ViewSelectors.newRentalStartDate);
	const endDate = useSelector(ViewSelectors.newRentalEndDate);
	const duration = useSelector(ViewSelectors.newRentalDuration);

	const endDateMoment = useMemo(() => moment(endDate), [endDate]);
	const startDateMoment = useMemo(() => moment(startDate), [startDate]);

	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone);
	const openingHoursWithExceptions = useSelector(
		ShopSelectors.activeLocationOpeningHoursWithException,
	);

	const incorrectStartDate = useMemo(() => moment(startDate).isBefore(moment()), [startDate]);

	const checkAndHandleStartDateUpdate = () => {
		if (moment(startDate).isBefore(moment())) {
			const newStartDate = getTimeNearestToNextQuarterHour().toISOString();
			handleStartDateChange(newStartDate);
		}
	};

	const startTimeSelectorType = useMemo((): TimeSelectorArgs => ({ type: 'startTime' }), []);

	const endTimeSelectorType = useMemo(
		(): TimeSelectorArgs => ({
			type: 'endTime',
			startTime: startDateMoment,
		}),
		[startDateMoment],
	);

	/** Periodically check that the rental start date is not in the past  */
	useInterval(checkAndHandleStartDateUpdate, 60 * 1000, { triggerOnInit: true });

	const handleRentNowToggle = (isRentNow: boolean) => {
		if (isRentNow) {
			const newStartDate = getTimeNearestToNextQuarterHour();
			if (!newStartDate.isSame(moment(startDate))) {
				handleStartDateChange(newStartDate.toISOString());
			}
		}
		dispatch(ViewActions.setRentNowToggle(isRentNow));
	};

	const handleStartDateChange = (date: string) => {
		const newEndDate = getNewEndTime(date, duration, openingHoursWithExceptions, shopTimezone);
		dispatch(ViewActions.setStartDate(date));
		dispatch(NewRentalActions.setRentalStartDate(date));
		dispatch(ViewActions.setEndDate(newEndDate));
		const newDuration: Duration = {
			...duration,
			durationInSeconds: moment(newEndDate).diff(moment(date), 'seconds'),
		};
		dispatch(ViewActions.setDuration(newDuration));
		dispatch(NewRentalActions.setOrderProductsDates(date, newEndDate, duration));
	};

	const handleEndDateChange = (date: string) => {
		dispatch(ViewActions.setEndDate(date));
		dispatch(NewRentalActions.setIsDurationManuallyChanged(true));
		const duration: Duration = {
			durationInSeconds: moment(date).diff(moment(startDate), 'seconds'),
			durationType: '24h',
			durationName: null,
		};
		dispatch(ViewActions.setDuration(duration));
		dispatch(NewRentalActions.setOrderProductsDates(startDate, date, duration));
	};

	return incorrectStartDate ? null : (
		<Box className={classes.wrapper}>
			<Container className={classes.container}>
				<Grid
					className={classes.grid}
					container
					spacing={0}
					justifyContent="space-around"
					alignItems="center"
				>
					<Grid item xs={12} md={6}>
						<Box p={1}>
							<RentNowToggle isRentNow={isRentNow} onChange={handleRentNowToggle} />
						</Box>
					</Grid>
					<Grid item xs={12} md={6}>
						<Box p={1}>
							<DurationSelect />
						</Box>
					</Grid>
					<Grid item xs={12} md={6}>
						<Collapse in={!isRentNow}>
							<Box p={1}>
								<StartTimeInput
									value={startDateMoment.toISOString()}
									onChange={handleStartDateChange}
									selectorType={startTimeSelectorType}
								/>
							</Box>
						</Collapse>
					</Grid>
					<Grid item xs={12} md={6}>
						<Collapse in={!isRentNow}>
							<Box p={1}>
								<StartTimeInput
									value={endDateMoment.toISOString()}
									selectorType={endTimeSelectorType}
									onChange={handleEndDateChange}
								/>
							</Box>
						</Collapse>
					</Grid>
				</Grid>
			</Container>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			background: theme.palette.colors.pru.main,
			paddingTop: 24,
			paddingBottom: 24,
		},
		container: {
			paddingTop: 0,
			paddingBottom: 0,
		},
		grid: {
			paddingTop: theme.spacing(1),
			paddingBottom: theme.spacing(1),
			marginLeft: theme.spacing(-1),
			marginRight: theme.spacing(-1),
			width: `calc(100% + ${theme.spacing(2)})`,
		},
	}),
);

export default TimeLocationSelector;
